import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarPlus,
  Copy,
  Info,
  Loader2,
  Pencil,
  QrCode,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocalizedName } from '@/components/localized-name';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';
import { TIME_SLOT_TABS } from './event-enums';
import { EventBookingsSheet } from './event-bookings-sheet';
import { TimeSlotQrDialog } from './time-slot-qr-dialog';
import { HostPicker, type PickedUser } from './host-picker';
import {
  fromApiTime,
  toApiTime,
  useCreateTimeSlot,
  useDeleteTimeSlot,
  useTimeSlots,
  useUpdateTimeSlot,
} from './events.api';
import type { BookingEvent, EventTimeSlot } from './types';

/**
 * Time slots for one event: create them, list them, edit and remove them.
 *
 * The four tabs are four server-side queries (`type` 0–3), each paginated
 * independently — the original kept all four in one `tabs` array of mutable
 * objects and mutated `tabs[type].loading` in place before every fetch.
 */

const OFFSET = env.timezoneOffset;

/**
 * Slot times are the venue's wall clock, and `fromApiTime` hands that clock
 * back in a Date's **UTC** fields. Everything below therefore reads and writes
 * UTC fields: touching `getHours`/`setHours` or formatting without a pinned
 * zone would add the browser's own offset on top of the configured one.
 */
const VENUE_ZONE = { timeZone: 'UTC' } as const;

/** `<input type="datetime-local">` wants `yyyy-MM-ddTHH:mm`. */
function toLocalInput(date: Date | null): string {
  if (!date) return '';
  return date.toISOString().slice(0, 16);
}

function parseLocalInput(value: string): Date | null {
  if (!value) return null;
  const date = new Date(`${value}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Slot times display shifted by the configured offset, as the original did. */
function slotWhen(value: string | null, locale: string) {
  const date = fromApiTime(value, OFFSET);
  if (!date) return '—';
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...VENUE_ZONE,
  }).format(date);
}

function durationMinutes(slot: EventTimeSlot): number | null {
  const start = fromApiTime(slot.startTime, OFFSET);
  const end = fromApiTime(slot.endTime, OFFSET);
  if (!start || !end) return null;
  return Math.round((end.getTime() - start.getTime()) / 60_000);
}

interface AdvancedForm {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  slotMinutes: number;
  bufferMinutes: number;
  capacity: number;
  createActive: boolean;
}

const DEFAULT_ADVANCED: AdvancedForm = {
  startDate: '',
  endDate: '',
  startTime: '09:00',
  endTime: '17:00',
  slotMinutes: 60,
  bufferMinutes: 0,
  capacity: 6,
  createActive: true,
};

/** Expands the advanced form into the individual slots it would create. */
function generateSlots(form: AdvancedForm): { start: Date; end: Date }[] {
  const startDate = parseLocalInput(`${form.startDate}T00:00`);
  const endDate = parseLocalInput(`${form.endDate}T00:00`);
  if (!startDate || !endDate || form.slotMinutes <= 0) return [];

  const [startHour, startMinute] = form.startTime.split(':').map(Number);
  const [endHour, endMinute] = form.endTime.split(':').map(Number);
  const days = Math.round(
    (endDate.getTime() - startDate.getTime()) / 86_400_000
  );
  if (days < 0) return [];

  const slots: { start: Date; end: Date }[] = [];
  for (let day = 0; day <= days; day += 1) {
    const dayStart = new Date(startDate);
    dayStart.setUTCDate(dayStart.getUTCDate() + day);
    dayStart.setUTCHours(startHour, startMinute, 0, 0);

    const dayEnd = new Date(startDate);
    dayEnd.setUTCDate(dayEnd.getUTCDate() + day);
    dayEnd.setUTCHours(endHour, endMinute, 0, 0);

    let cursor = dayStart;
    while (cursor < dayEnd) {
      const end = new Date(cursor.getTime() + form.slotMinutes * 60_000);
      slots.push({ start: cursor, end });
      cursor = new Date(
        cursor.getTime() + (form.slotMinutes + form.bufferMinutes) * 60_000
      );
    }
  }
  return slots;
}

function CapacityBadge({ slot, t }: { slot: EventTimeSlot; t: (k: string) => string }) {
  const booked = slot.currentCapacity ?? 0;
  const max = slot.maxCapacity ?? 0;
  if (booked > max) return <Badge variant="destructive">{t('events.overBooked')}</Badge>;
  if (booked === max && max > 0) return <Badge variant="warning">{t('events.booked')}</Badge>;
  return <Badge variant="success">{t('events.available')}</Badge>;
}

export function EventTimeSlotsSheet({
  event,
  open,
  onOpenChange,
}: {
  event: BookingEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'es' ? 'es-PR' : 'en-US';

  const [activeTab, setActiveTab] = useState('0');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  // --- basic create
  const [basicStart, setBasicStart] = useState('');
  const [basicEnd, setBasicEnd] = useState('');
  const [basicCapacity, setBasicCapacity] = useState(6);
  const [basicActive, setBasicActive] = useState(true);
  const [basicHost, setBasicHost] = useState<PickedUser | null>(null);

  // --- advanced create
  const [advanced, setAdvanced] = useState<AdvancedForm>(DEFAULT_ADVANCED);
  const [advancedHost, setAdvancedHost] = useState<PickedUser | null>(null);
  const [preview, setPreview] = useState<{ start: Date; end: Date }[] | null>(
    null
  );

  // --- modals
  const [editing, setEditing] = useState<EventTimeSlot | null>(null);
  const [conflicts, setConflicts] = useState<EventTimeSlot[] | null>(null);
  const [pendingForce, setPendingForce] = useState<(() => void) | null>(null);
  const [qrFor, setQrFor] = useState<EventTimeSlot | null>(null);
  const [bookingsFor, setBookingsFor] = useState<EventTimeSlot | null>(null);

  const type = Number(activeTab);
  const slots = useTimeSlots({
    eventId: event?.id ?? null,
    type,
    page,
    size,
    enabled: open,
  });

  const createSlot = useCreateTimeSlot();
  const updateSlot = useUpdateTimeSlot();
  const deleteSlot = useDeleteTimeSlot();

  /* A slot sharing a start time with another on the same event. */
  const rows = useMemo(() => {
    const list = slots.data?.timeSlots ?? [];
    return list.map((slot) => ({
      ...slot,
      duplicate:
        list.filter((other) => other.startTime === slot.startTime).length > 1,
    }));
  }, [slots.data]);

  if (!event) return null;

  const fail = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('events.slotActionFailed')
    );

  /**
   * The create endpoint answers with the *conflicting slots* rather than an
   * error when there are any; re-sending with `Force` is what commits it.
   */
  function submitCreate(input: Parameters<typeof createSlot.mutate>[0]) {
    createSlot.mutate(input, {
      onSuccess: (conflicting) => {
        if (Array.isArray(conflicting) && conflicting.length > 0) {
          setConflicts(conflicting);
          setPendingForce(() => () => submitCreate({ ...input, force: true }));
          return;
        }
        setConflicts(null);
        setPendingForce(null);
        toast.success(t('events.slotCreated'));
        void slots.refetch();
      },
      onError: fail,
    });
  }

  function createBasic(force = false) {
    const start = parseLocalInput(basicStart);
    const end = parseLocalInput(basicEnd);
    if (!start || !end) {
      toast.error(t('events.pickStartAndEnd'));
      return;
    }
    submitCreate({
      startTime: toApiTime(start, OFFSET),
      endTime: toApiTime(end, OFFSET),
      maxCapacity: basicCapacity,
      eventId: event!.id,
      isActive: basicActive,
      force: force,
      timeSlotHostId: basicHost?.id ?? null,
    });
  }

  /** The advanced generator posts one slot at a time, as the original did. */
  async function createAdvanced() {
    if (!preview?.length) return;
    for (const slot of preview) {
      try {
        await createSlot.mutateAsync({
          startTime: toApiTime(slot.start, OFFSET),
          endTime: toApiTime(slot.end, OFFSET),
          maxCapacity: advanced.capacity,
          eventId: event!.id,
          isActive: advanced.createActive,
          force: true,
          timeSlotHostId: advancedHost?.id ?? null,
        });
      } catch (error) {
        fail(error);
        return;
      }
    }
    toast.success(t('events.slotsCreated', { count: preview.length }));
    setPreview(null);
    void slots.refetch();
  }

  function cloneSlot(slot: EventTimeSlot) {
    submitCreate({
      startTime: slot.startTime!,
      endTime: slot.endTime!,
      maxCapacity: slot.maxCapacity ?? 0,
      eventId: event!.id,
      isActive: slot.isActive,
      force: true,
      timeSlotHostId: slot.timeSlotHostId,
    });
  }

  return (
    <TooltipProvider>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle className="flex flex-wrap items-baseline gap-x-2">
              {t('events.timeSlotsFor')}
              {/* The internal name rides above, as it does on every other
                  place an event is named. */}
              <span className="text-muted-foreground font-normal">
                <LocalizedName
                  nameEn={event?.nameEn}
                  nameEs={event?.nameEs}
                  internalName={event?.name}
                />
              </span>
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-6 p-4">
            {/* ------------------------------------------------ create */}
            <section className="rounded-xl border p-3">
              <Tabs defaultValue="basic">
                <TabsList>
                  <TabsTrigger value="basic">{t('events.basic')}</TabsTrigger>
                  <TabsTrigger value="advanced">
                    {t('events.advanced')}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.startTime')}
                      </Label>
                      <Input
                        type="datetime-local"
                        value={basicStart}
                        onChange={(e) => setBasicStart(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.endTime')}
                      </Label>
                      <Input
                        type="datetime-local"
                        value={basicEnd}
                        onChange={(e) => setBasicEnd(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">
                      {t('events.capacity')}
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={basicCapacity}
                      onChange={(e) => setBasicCapacity(Number(e.target.value))}
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <Label className="text-xs font-semibold">
                      {t('events.shouldBeActive')}
                    </Label>
                    <Switch
                      checked={basicActive}
                      onCheckedChange={setBasicActive}
                    />
                  </div>

                  <HostPicker
                    label={t('events.selectedHost')}
                    selected={basicHost}
                    onSelect={setBasicHost}
                  />

                  <Button
                    className="w-full"
                    disabled={createSlot.isPending}
                    onClick={() => createBasic()}
                  >
                    {createSlot.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <CalendarPlus />
                    )}
                    {t('events.create')}
                  </Button>
                </TabsContent>

                <TabsContent value="advanced" className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.rangeStart')}
                      </Label>
                      <Input
                        type="date"
                        value={advanced.startDate}
                        onChange={(e) =>
                          setAdvanced({ ...advanced, startDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.rangeEnd')}
                      </Label>
                      <Input
                        type="date"
                        value={advanced.endDate}
                        onChange={(e) =>
                          setAdvanced({ ...advanced, endDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.startTime')}
                      </Label>
                      <Input
                        type="time"
                        value={advanced.startTime}
                        onChange={(e) =>
                          setAdvanced({ ...advanced, startTime: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.endTime')}
                      </Label>
                      <Input
                        type="time"
                        value={advanced.endTime}
                        onChange={(e) =>
                          setAdvanced({ ...advanced, endTime: e.target.value })
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1 text-xs font-semibold">
                        {t('events.slotMinutes')}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('events.slotMinutesHint')}
                          </TooltipContent>
                        </Tooltip>
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={advanced.slotMinutes}
                        onChange={(e) =>
                          setAdvanced({
                            ...advanced,
                            slotMinutes: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1 text-xs font-semibold">
                        {t('events.bufferMinutes')}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('events.bufferMinutesHint')}
                          </TooltipContent>
                        </Tooltip>
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        value={advanced.bufferMinutes}
                        onChange={(e) =>
                          setAdvanced({
                            ...advanced,
                            bufferMinutes: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {t('events.capacity')}
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={advanced.capacity}
                        onChange={(e) =>
                          setAdvanced({
                            ...advanced,
                            capacity: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <Label className="text-xs font-semibold">
                      {t('events.shouldBeActive')}
                    </Label>
                    <Switch
                      checked={advanced.createActive}
                      onCheckedChange={(createActive) =>
                        setAdvanced({ ...advanced, createActive })
                      }
                    />
                  </div>

                  <HostPicker
                    label={t('events.selectedHost')}
                    selected={advancedHost}
                    onSelect={setAdvancedHost}
                  />

                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => setPreview(generateSlots(advanced))}
                  >
                    {t('events.preview')}
                  </Button>
                </TabsContent>
              </Tabs>
            </section>

            {/* ------------------------------------------------- lists */}
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Tabs
                  value={activeTab}
                  onValueChange={(value) => {
                    setActiveTab(value);
                    setPage(0);
                  }}
                  className="min-w-0 flex-1"
                >
                  <TabsList>
                    {TIME_SLOT_TABS.map((tab) => (
                      <TabsTrigger key={tab.type} value={String(tab.type)}>
                        {t(tab.labelKey)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.reload')}
                  onClick={() => void slots.refetch()}
                  disabled={slots.isFetching}
                >
                  <RefreshCw
                    className={cn('size-4', slots.isFetching && 'animate-spin')}
                  />
                </Button>
              </div>

              {slots.isPending && (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="space-y-2 rounded-xl border p-3">
                      <div className="flex justify-between gap-3">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-5 w-16" />
                      </div>
                      <Skeleton className="h-3 w-52" />
                    </div>
                  ))}
                </div>
              )}

              {slots.isError && (
                <p className="text-muted-foreground py-10 text-center text-sm">
                  {t('events.slotsFailed')}
                </p>
              )}

              {slots.data && rows.length === 0 && (
                <div className="relative rounded-lg">
                  <BusyOverlay show={slots.isFetching} />
                  <p
                    className={cn(
                      'text-muted-foreground rounded-lg border border-dashed py-10 text-center text-sm',
                      slots.isFetching && 'opacity-60 transition-opacity'
                    )}
                  >
                    {t('events.noSlots')}
                  </p>
                </div>
              )}

              {rows.length > 0 && (
                <div className="relative space-y-3">
                  <BusyOverlay show={slots.isFetching} />
                  {rows.map((slot) => {
                    const minutes = durationMinutes(slot);
                    return (
                      <div
                        key={slot.id}
                        className={cn(
                          'bg-card space-y-2 rounded-xl border p-3',
                          slots.isFetching && 'opacity-60 transition-opacity'
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium">
                              {slotWhen(slot.startTime, locale)}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {t('events.until')}{' '}
                              {slotWhen(slot.endTime, locale)}
                              {minutes !== null && ` · ${minutes} ${t('events.min')}`}
                            </p>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            <CapacityBadge slot={slot} t={t} />
                            <span className="text-muted-foreground text-xs tabular-nums">
                              {slot.currentCapacity ?? 0}/{slot.maxCapacity ?? 0}
                            </span>
                          </div>
                        </div>

                        {slot.duplicate && (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="size-3" />
                            {t('events.duplicate')}
                          </Badge>
                        )}

                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            onClick={() => setBookingsFor(slot)}
                          >
                            <Users className="size-3.5" />
                            {t('events.bookings')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(slot)}
                          >
                            <Pencil className="size-3.5" />
                            {t('events.edit')}
                          </Button>
                          <Button
                            variant="outline"
                            size="icon-sm"
                            aria-label={t('events.qrCode')}
                            onClick={() => setQrFor(slot)}
                          >
                            <QrCode className="size-3.5" />
                          </Button>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon-sm"
                                aria-label={t('events.cloneSlot')}
                              >
                                <Copy className="size-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t('events.cloneSlot')}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('events.cloneSlotBody')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  {t('common.cancel')}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => cloneSlot(slot)}
                                >
                                  {t('events.clone')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="icon-sm"
                                aria-label={t('events.removeSlot')}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t('events.removeSlot')}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('events.removeSlotBody')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>
                                  {t('common.cancel')}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() =>
                                    deleteSlot.mutate(slot.id, {
                                      onSuccess: () => {
                                        toast.success(t('events.slotRemoved'));
                                        void slots.refetch();
                                      },
                                      onError: fail,
                                    })
                                  }
                                >
                                  {t('events.remove')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {(slots.data?.total ?? 0) > 0 && (
                <Pagination
                  page={page}
                  size={size}
                  total={slots.data?.total ?? 0}
                  onPageChange={setPage}
                  onSizeChange={(next) => {
                    setSize(next);
                    setPage(0);
                  }}
                />
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>

      {/* ------------------------------------------------ edit modal */}
      <EditTimeSlotDialog
        slot={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onSave={(values) =>
          updateSlot.mutate(values, {
            onSuccess: () => {
              toast.success(t('events.slotUpdated'));
              setEditing(null);
              void slots.refetch();
            },
            onError: fail,
          })
        }
        saving={updateSlot.isPending}
      />

      {/* -------------------------------------------- conflicts modal */}
      <AlertDialog
        open={conflicts !== null}
        onOpenChange={(next) => {
          if (!next) {
            setConflicts(null);
            setPendingForce(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('events.conflictingEvents')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('events.conflictsBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-56 space-y-1.5 overflow-y-auto text-sm">
            {(conflicts ?? []).map((conflict) => (
              <div key={conflict.id} className="rounded-lg border p-2">
                <p>{slotWhen(conflict.startTime, locale)}</p>
                <p className="text-muted-foreground text-xs">
                  {t('events.until')} {slotWhen(conflict.endTime, locale)}
                </p>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('orders.close')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => pendingForce?.()}>
              {t('events.createAnyway')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------------------------------------------- preview modal */}
      <AlertDialog
        open={preview !== null}
        onOpenChange={(next) => {
          if (!next) setPreview(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('events.slotsToCreate', { count: preview?.length ?? 0 })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('events.slotsToCreateBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-56 space-y-1 overflow-y-auto text-sm">
            {(preview ?? []).map((slot, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded border px-2 py-1"
              >
                <span>
                  {new Intl.DateTimeFormat(locale, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    ...VENUE_ZONE,
                  }).format(slot.start)}
                </span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {Math.round(
                    (slot.end.getTime() - slot.start.getTime()) / 60_000
                  )}{' '}
                  {t('events.min')}
                </span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('orders.close')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void createAdvanced()}
              disabled={!preview?.length}
            >
              {t('events.create')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TimeSlotQrDialog
        event={event}
        timeSlot={qrFor}
        open={qrFor !== null}
        onOpenChange={(next) => {
          if (!next) setQrFor(null);
        }}
      />

      <EventBookingsSheet
        event={event}
        timeSlot={bookingsFor}
        open={bookingsFor !== null}
        onOpenChange={(next) => {
          if (!next) setBookingsFor(null);
        }}
      />
    </TooltipProvider>
  );
}

// --------------------------------------------------------------- edit modal

function EditTimeSlotDialog({
  slot,
  open,
  onOpenChange,
  onSave,
  saving,
}: {
  slot: EventTimeSlot | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (values: {
    timeSlotId: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    isActive: boolean;
    timeSlotHostId?: string | null;
  }) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [capacity, setCapacity] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [host, setHost] = useState<PickedUser | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);

  // Hydrate once per slot; the dialog stays mounted between openings.
  if (slot && hydrated !== slot.id) {
    setHydrated(slot.id);
    setStart(toLocalInput(fromApiTime(slot.startTime, OFFSET)));
    setEnd(toLocalInput(fromApiTime(slot.endTime, OFFSET)));
    setCapacity(slot.maxCapacity ?? 0);
    setIsActive(slot.isActive);
    setHost(null);
  }

  if (!slot) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('events.editTimeSlot')}</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            {t('events.editTimeSlot')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs font-semibold">
              {t('events.isActive')}
            </Label>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                {t('events.startTime')}
              </Label>
              <Input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                {t('events.endTime')}
              </Label>
              <Input
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('events.capacity')}
            </Label>
            <Input
              type="number"
              min={0}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value))}
            />
          </div>

          <HostPicker
            label={t('events.host')}
            selected={host}
            onSelect={setHost}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('orders.close')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={saving}
            onClick={() => {
              const startDate = parseLocalInput(start);
              const endDate = parseLocalInput(end);
              if (!startDate || !endDate) return;
              onSave({
                timeSlotId: slot.id,
                // The original applied the offset on create but not on update,
                // so an edit stored the venue's wall clock as though it were
                // UTC and moved the slot back by the offset. Both paths convert
                // the same way now.
                startTime: toApiTime(startDate, OFFSET),
                endTime: toApiTime(endDate, OFFSET),
                maxCapacity: capacity,
                isActive: isActive,
                timeSlotHostId: host?.id ?? slot.timeSlotHostId,
              });
            }}
          >
            {saving ? <Loader2 className="animate-spin" /> : t('events.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
